import { useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const retailerBadgeStyles = {
  walmart: "bg-blue-100 text-blue-800",
  sobeys: "bg-green-100 text-green-800",
};

function BolField({ label, value, onChange, readOnly = false }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700">{label}</label>
      <input
        type="text"
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm ${
          readOnly
            ? "bg-gray-50 text-gray-200"
            : "focus:border-teal-600 focus:ring-teal-600"
        }`}
      />
    </div>
  );
}

function SunBolPreview({ bolData, labels, showWfp }) {
  const locationHeader = labels?.location_header ?? "DC number and Address:";
  const retailerItemHeader =
    labels?.retailer_item_header ?? "Walmart Item Numbers:";
  const stockColumnHeader =
    labels?.stock_column_header ?? "Supplier Stock Numbers:";

  return (
    <div className="rounded border border-gray-300 bg-white p-2">
      <table className="w-full border-collapse text-[10px]">
        <tbody>
          <tr>
            <td className="border border-black p-1 text-center text-sm font-bold" colSpan={4}>
              BILL OF LADING &amp; PACKING SLIP
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 text-center font-bold" colSpan={2}>
              PO# {bolData.po_number}
            </td>
            <td className="border border-black p-1 font-bold" colSpan={2}>
              BOL# {bolData.bol_number}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">
              Supplier&apos;s Name &amp; Address:
            </td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.supplier?.name}
              <br />
              {bolData.supplier?.address}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Supplier&apos;s Number:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.supplier_number}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold" rowSpan={2}>
              {locationHeader}
            </td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.ship_to?.name}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.ship_to?.full_address}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">PO number:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.po_number}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Invoice number:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.invoice_number}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">SSCC HRI Number</td>
            <td className="border border-black p-1 font-bold">{stockColumnHeader}</td>
            <td className="border border-black p-1 font-bold">{retailerItemHeader}</td>
            <td className="border border-black p-1 font-bold">
              Total Case Count Per Item:
            </td>
          </tr>
          {(bolData.line_items ?? []).map((item, index) => (
            <tr key={index}>
              <td className="border border-black p-1">{item.sscc_hri_number}</td>
              <td className="border border-black p-1">{item.supplier_stock_number}</td>
              <td className="border border-black p-1">{item.retailer_item_number}</td>
              <td className="border border-black p-1">{item.case_count} case</td>
            </tr>
          ))}
          <tr>
            <td className="border border-black p-1 font-bold">Total Case Count:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.total_case_count}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">
              Pallet count - list by type of pallet used:
            </td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.chep_pallet_count}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">
              Total weight of freight on trailer:
            </td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.total_freight_weight}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Carrier name:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.carrier_name}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Seal number:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.seal_number}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Prepaid Only:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.prepaid_text}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">
              Appointment number:
            </td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.appointment_number}
            </td>
          </tr>
          {showWfp && (
            <>
              <tr>
                <td className="border border-black p-1 font-bold">WFP Only:</td>
                <td className="border border-black p-1" colSpan={3}>
                  {bolData.wfp?.text}
                </td>
              </tr>
              <tr>
                <td className="border border-black p-1 font-bold">Load I.D. Number:</td>
                <td className="border border-black p-1" colSpan={3}>
                  {bolData.wfp?.load_id_number}
                </td>
              </tr>
              <tr>
                <td className="border border-black p-1 font-bold">Pick up date:</td>
                <td className="border border-black p-1" colSpan={3}>
                  {bolData.wfp?.pickup_date}
                </td>
              </tr>
              <tr>
                <td className="border border-black p-1 font-bold">
                  Gate In &amp; Gate Out Times:
                </td>
                <td className="border border-black p-1" colSpan={3}>
                  {bolData.wfp?.gate_in_gate_out_times}
                </td>
              </tr>
            </>
          )}
          <tr>
            <td className="border border-black p-1 font-bold">When Applicable:</td>
            <td className="border border-black p-1" colSpan={3}></td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Product</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.product_description}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Temperature recorder number:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.temperature_recorder_number}
            </td>
          </tr>
          <tr>
            <td className="border border-black p-1 font-bold">Required Temperature:</td>
            <td className="border border-black p-1" colSpan={3}>
              {bolData.required_temperature}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const emptyLineItem = () => ({
  sscc_hri_number: "",
  supplier_stock_number: "",
  retailer_item_number: "",
  case_count: "",
});

export default function SunBolReviewModal(props) {
  const {
    setSlideOver,
    initialBolData,
    onPrint,
    onReset,
    isPrinting,
  } = props;

  const [bolData, setBolData] = useState(initialBolData);

  const retailerLabel = useMemo(() => {
    if (bolData.bol_type === "sobeys") return "Sobeys";
    return "Walmart";
  }, [bolData.bol_type]);

  const showWfp = bolData.bol_type === "walmart" && bolData.wfp?.visible !== false;

  const updateField = (field, value) => {
    setBolData((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (group, field, value) => {
    setBolData((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [field]: value,
      },
    }));
  };

  const updateLineItem = (index, field, value) => {
    setBolData((prev) => {
      const line_items = [...(prev.line_items ?? [])];
      line_items[index] = { ...line_items[index], [field]: value };
      return { ...prev, line_items };
    });
  };

  const addLineItem = () => {
    setBolData((prev) => ({
      ...prev,
      line_items: [...(prev.line_items ?? []), emptyLineItem()],
    }));
  };

  const removeLineItem = (index) => {
    setBolData((prev) => ({
      ...prev,
      line_items: (prev.line_items ?? []).filter((_, i) => i !== index),
    }));
  };

  const recalculateTotalCases = () => {
    const total = (bolData.line_items ?? []).reduce(
      (sum, item) => sum + (parseFloat(item.case_count) || 0),
      0,
    );
    updateField("total_case_count", String(total));
  };

  const handleReset = async () => {
    const resetData = await onReset();
    if (resetData) {
      setBolData(resetData);
    }
  };

  const closePanel = () => {
    setSlideOver({
      childComponent: null,
      open: false,
      customClass: null,
    });
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="px-4 py-6 sm:px-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-base font-semibold leading-6 text-gray-900">
                Review Bill of Lading
              </h2>
              <span
                className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  retailerBadgeStyles[bolData.bol_type] ??
                  retailerBadgeStyles.walmart
                }`}
              >
                {retailerLabel}
              </span>
            </div>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={closePanel}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="relative flex flex-1 flex-col gap-6 px-4 pb-6 sm:px-6 xl:flex-row">
        <div className="w-full space-y-4 xl:w-[48%]">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <BolField
              label="PO number"
              value={bolData.po_number}
              onChange={(value) => updateField("po_number", value)}
            />
            <BolField
              label="BOL number"
              value={bolData.bol_number}
              onChange={(value) => updateField("bol_number", value)}
            />
            <BolField
              label="Invoice number"
              value={bolData.invoice_number}
              onChange={(value) => updateField("invoice_number", value)}
            />
            <BolField
              label="Supplier number"
              value={bolData.supplier_number}
              onChange={(value) => updateField("supplier_number", value)}
            />
            <BolField
              label="Product"
              value={bolData.product_description}
              onChange={(value) => updateField("product_description", value)}
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Ship to</h4>
            <BolField
              label="Location name"
              value={bolData.ship_to?.name}
              onChange={(value) => updateNestedField("ship_to", "name", value)}
            />
            <BolField
              label="Full address"
              value={bolData.ship_to?.full_address}
              onChange={(value) =>
                updateNestedField("ship_to", "full_address", value)
              }
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Line items</h4>
              <button
                type="button"
                className="text-xs font-semibold text-teal-700 hover:text-teal-800"
                onClick={addLineItem}
              >
                + Add row
              </button>
            </div>
            {(bolData.line_items ?? []).map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 p-3 sm:grid-cols-5"
              >
                <BolField
                  label="SSCC HRI #"
                  value={item.sscc_hri_number}
                  onChange={(value) =>
                    updateLineItem(index, "sscc_hri_number", value)
                  }
                />
                <BolField
                  label="Supplier stock #"
                  value={item.supplier_stock_number}
                  onChange={(value) =>
                    updateLineItem(index, "supplier_stock_number", value)
                  }
                />
                <BolField
                  label="Retailer item #"
                  value={item.retailer_item_number}
                  onChange={(value) =>
                    updateLineItem(index, "retailer_item_number", value)
                  }
                />
                <BolField
                  label="Case count"
                  value={item.case_count}
                  onChange={(value) =>
                    updateLineItem(index, "case_count", value)
                  }
                />
                <div className="flex items-end">
                  <button
                    type="button"
                    className="rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100"
                    onClick={() => removeLineItem(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="text-xs font-semibold text-gray-600 hover:text-gray-800"
              onClick={recalculateTotalCases}
            >
              Recalculate total cases from line items
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <BolField
              label="Total case count"
              value={bolData.total_case_count}
              onChange={(value) => updateField("total_case_count", value)}
            />
            <BolField
              label="Chep pallet count"
              value={bolData.chep_pallet_count}
              onChange={(value) => updateField("chep_pallet_count", value)}
            />
            <BolField
              label="Total freight weight (kg)"
              value={bolData.total_freight_weight}
              onChange={(value) => updateField("total_freight_weight", value)}
            />
            <BolField
              label="Carrier name"
              value={bolData.carrier_name}
              onChange={(value) => updateField("carrier_name", value)}
            />
            <BolField
              label="Seal number"
              value={bolData.seal_number}
              onChange={(value) => updateField("seal_number", value)}
            />
            <BolField
              label="Prepaid"
              value={bolData.prepaid_text}
              onChange={(value) => updateField("prepaid_text", value)}
            />
            <BolField
              label="Appointment number"
              value={bolData.appointment_number}
              onChange={(value) => updateField("appointment_number", value)}
            />
            <BolField
              label="Temperature recorder number"
              value={bolData.temperature_recorder_number}
              onChange={(value) =>
                updateField("temperature_recorder_number", value)
              }
            />
            <BolField
              label="Required temperature"
              value={bolData.required_temperature}
              readOnly
              onChange={() => {}}
            />
          </div>

          {showWfp && (
            <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <h4 className="text-sm font-semibold text-gray-900">WFP (Walmart only)</h4>
              <BolField
                label="WFP text"
                value={bolData.wfp?.text}
                onChange={(value) => updateNestedField("wfp", "text", value)}
              />
              <BolField
                label="Load I.D. number"
                value={bolData.wfp?.load_id_number}
                onChange={(value) =>
                  updateNestedField("wfp", "load_id_number", value)
                }
              />
              <BolField
                label="Pick up date"
                value={bolData.wfp?.pickup_date}
                onChange={(value) =>
                  updateNestedField("wfp", "pickup_date", value)
                }
              />
              <BolField
                label="Gate in & gate out times"
                value={bolData.wfp?.gate_in_gate_out_times}
                onChange={(value) =>
                  updateNestedField("wfp", "gate_in_gate_out_times", value)
                }
              />
            </div>
          )}
        </div>

        <div className="w-full xl:w-[52%]">
          <h4 className="mb-2 text-sm font-semibold text-gray-900">Preview</h4>
          <SunBolPreview
            bolData={bolData}
            labels={bolData.labels}
            showWfp={showWfp}
          />
        </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-end gap-3 px-4 py-4 sm:px-6">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={closePanel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleReset}
          disabled={isPrinting}
        >
          Reset to defaults
        </button>
        <button
          type="button"
          className="rounded-md bg-slate-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          onClick={() => onPrint(bolData)}
          disabled={isPrinting}
        >
          {isPrinting ? "Generating..." : "Generate BOL"}
        </button>
      </div>
    </div>
  );
}
